import React, { useCallback, useEffect, useState } from 'react'
import { cobrandingLogoSelector } from 'ducks/ui'
import { useSelector } from 'react-redux'
import CobrandingLogo from 'views/components/UI/Logo/logo-cobranding'
import { TestLayout } from 'views/layouts'
import { ProgressBar, CompetenceCard, InstructionsText, QuestionView } from './components'
import { useCompetences, useTest360Answers, useTest360Data } from './hooks'
import { Container, Header, Content, CompetencesGrid } from './styled'
import { useNotification } from 'lib/context/notification.context'
import test360GetCompetencesForUser from 'services/process/test360/test360GetCompetencesForUser'
import { Competence } from './types'
import { userTestIdSelector } from 'ducks/evaluatedUser'
import ModalLoader from 'views/components/UI/ModalLoader'
import { useNavigate } from 'react-router-dom'
import { formatDateToSpanish } from 'utils/formatDate'
import test360FinishTest from 'services/process/test360/test360FinishTest'

interface CurrentView {
  type: 'competences' | 'questions'
  competenceId?: number
  questionIndex?: number
}

const QuestionsTest360 = () => {
  const { getSuccess, getError } = useNotification()
  const navigate = useNavigate()
  const companyLogo = useSelector(cobrandingLogoSelector)
  const userTestId = useSelector(userTestIdSelector)
  const {
    saveUserAnswer,
    saveAllAnswersToAPI,
    getAnsweredQuestionsCount,
    getQuestionAnswers,
    getPeopleToEvaluate,
    canProceedToNextQuestion,
  } = useTest360Answers()
  const { competences, loadTest360Data } = useTest360Data()
  const {
    competences: processedCompetences,
    completedCount,
    totalCount,
  } = useCompetences([], getAnsweredQuestionsCount)

  const [currentView, setCurrentView] = useState<CurrentView>({ type: 'competences' })
  const [loading, setLoading] = useState(false)
  const [finalDate, setFinalDate] = useState<string>('')

  // Use processedCompetences if available, otherwise use competences from API
  const displayCompetences = processedCompetences.length > 0 ? processedCompetences : competences

  useEffect(() => {
    setLoading(true)
    test360GetCompetencesForUser(userTestId)
      .then((res) => {
        if (res?.data) {
          loadTest360Data(res.data)
          setFinalDate(res.data.finalDate || '')
        }
        setLoading(false)
      })
      .catch((error) => {
        console.error('Error fetching competences:', error)
        setLoading(false)
      })
  }, [loadTest360Data, userTestId])

  const handleStartCompetence = useCallback((competence: Competence) => {
    setCurrentView({
      type: 'questions',
      competenceId: competence.id,
      questionIndex: 0,
    })
  }, [])

  const handleContinueCompetence = useCallback(
    (competence: Competence) => {
      const questionAnswers = getQuestionAnswers(competence.id, competence.questions.length)
      const peopleToEvaluate = getPeopleToEvaluate()
      let nextQuestionIndex = 0

      for (let i = 0; i < competence.questions.length; i++) {
        const answers = questionAnswers[i] || {}
        const allPeopleEvaluated = peopleToEvaluate.every((person: any) => answers[person.id] !== undefined)

        if (!allPeopleEvaluated) {
          nextQuestionIndex = i
          break
        }
      }

      setCurrentView({
        type: 'questions',
        competenceId: competence.id,
        questionIndex: nextQuestionIndex,
      })
    },
    [getQuestionAnswers, getPeopleToEvaluate]
  )

  const handleAnswerQuestion = useCallback(
    async (questionIndex: number, evaluatedUserId: string, score: number) => {
      if (currentView.competenceId) {
        await saveUserAnswer(currentView.competenceId, questionIndex, evaluatedUserId, score)
      }
    },
    [currentView.competenceId, saveUserAnswer]
  )

  const handleNextQuestion = useCallback(async () => {
    if (!currentView.competenceId || currentView.questionIndex === undefined) return

    const currentCompetence = displayCompetences.find((c) => c.id === currentView.competenceId)
    if (!currentCompetence) return

    const isLastQuestion = currentView.questionIndex === currentCompetence.questions.length - 1

    // Para la última pregunta, no necesitamos verificar canProceedToNextQuestion
    // ya que las respuestas se guardaron inmediatamente al seleccionarlas
    if (!isLastQuestion) {
      const canProceed = canProceedToNextQuestion(currentView.competenceId, currentView.questionIndex)
      if (!canProceed) {
        return
      }
    }

    setLoading(true)

    // Save all answers to API with timeout
    try {
      const timeoutPromise = new Promise(
        (_, reject) => setTimeout(() => reject(new Error('Timeout')), 30000) // 30 second timeout
      )

      await Promise.race([saveAllAnswersToAPI(), timeoutPromise])
    } catch (error) {
      console.error('Error saving answers to API:', error)
      setLoading(false)
      getError('Hay un problema con la conexión. Por favor, inténtalo nuevamente.')
      return
    }

    if (isLastQuestion) {
      // Encontrar el índice de la competencia actual
      const currentCompetenceIndex = displayCompetences.findIndex((c) => c.id === currentView.competenceId)
      const isLastCompetence = currentCompetenceIndex === displayCompetences.length - 1

      if (isLastCompetence) {
        const finishedTest = await test360FinishTest(userTestId)
        if (finishedTest.status === 'OK') {
          getSuccess('Has finalizado la evaluación 360° exitosamente.')
          navigate('/')
          setLoading(false)
          setCurrentView({ type: 'competences' })
        } else {
          setLoading(false)
          getError('Error al finalizar la evaluación. Por favor, inténtalo de nuevo.')
        }
      } else {
        // Ir a la primera pregunta de la siguiente competencia
        const nextCompetence = displayCompetences[currentCompetenceIndex + 1]
        if (nextCompetence) {
          setCurrentView({
            type: 'questions',
            competenceId: nextCompetence.id,
            questionIndex: 0,
          })
        }
        setLoading(false)
      }
    } else {
      // Ir a la siguiente pregunta de la misma competencia
      setCurrentView({
        ...currentView,
        questionIndex: currentView.questionIndex + 1,
      })
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, displayCompetences, canProceedToNextQuestion, saveAllAnswersToAPI])

  const handleSaveAndContinueLater = useCallback(async () => {
    setLoading(true)

    try {
      // Save all answers to API with timeout
      const timeoutPromise = new Promise(
        (_, reject) => setTimeout(() => reject(new Error('Timeout')), 30000) // 30 second timeout
      )

      await Promise.race([saveAllAnswersToAPI(), timeoutPromise])

      // Pequeño delay para asegurar que todas las respuestas se han procesado
      await new Promise((resolve) => setTimeout(resolve, 100))

      navigate('/')
      setCurrentView({ type: 'competences' })
      getSuccess('Tus respuestas han sido guardadas exitosamente. Puedes retomar la evaluación 360° más adelante.')
    } catch (error) {
      console.error('Error saving answers:', error)
      setLoading(false)
      getError('Hay un problema con la conexión. Por favor, inténtalo nuevamente.')
      return
    } finally {
      setLoading(false)
    }
  }, [saveAllAnswersToAPI, navigate, getSuccess, getError])

  const handleBackToQuestions = useCallback(() => {
    if (!currentView.competenceId || currentView.questionIndex === undefined) return

    if (currentView.questionIndex > 0) {
      setCurrentView({
        ...currentView,
        questionIndex: currentView.questionIndex - 1,
      })
    } else {
      setCurrentView({ type: 'competences' })
    }
  }, [currentView])

  if (currentView.type === 'questions' && currentView.competenceId && currentView.questionIndex !== undefined) {
    const currentCompetence = displayCompetences.find((c) => c.id === currentView.competenceId)
    if (!currentCompetence || !currentCompetence.questions || !Array.isArray(currentCompetence.questions)) {
      setCurrentView({ type: 'competences' })
      return null
    }

    // Find the sequential number of the current competence
    const competenceSequentialNumber = displayCompetences.findIndex((c) => c.id === currentView.competenceId) + 1
    const currentCompetenceIndex = displayCompetences.findIndex((c) => c.id === currentView.competenceId)
    const isLastCompetence = currentCompetenceIndex === displayCompetences.length - 1

    const questionAnswers = getQuestionAnswers(currentView.competenceId, currentCompetence.questions.length)

    const safeQuestionAnswers = questionAnswers && typeof questionAnswers === 'object' ? questionAnswers : {}

    return (
      <TestLayout cobrandingLogoUrl={companyLogo}>
        <QuestionView
          competence={currentCompetence}
          currentQuestionIndex={currentView.questionIndex}
          answeredQuestions={safeQuestionAnswers}
          peopleToEvaluate={getPeopleToEvaluate()}
          onAnswerQuestion={handleAnswerQuestion}
          onNext={handleNextQuestion}
          onBack={handleBackToQuestions}
          onSaveAndContinueLater={handleSaveAndContinueLater}
          totalCompetences={totalCount}
          completedCompetences={completedCount}
          companyLogo={companyLogo}
          competenceSequentialNumber={competenceSequentialNumber}
          isLastCompetence={isLastCompetence}
          isLoading={loading}
        />
      </TestLayout>
    )
  }

  return (
    <TestLayout cobrandingLogoUrl={companyLogo}>
      <Container>
        <Header>{companyLogo && <CobrandingLogo logoUrl={companyLogo} hideMobile />}</Header>

        <Content>
          <ProgressBar title="Evaluación 360°" totalCompetences={totalCount} completedCompetences={completedCount} />

          <InstructionsText date={formatDateToSpanish(finalDate)} />

          <CompetencesGrid>
            {displayCompetences.map((competence, index) => (
              <CompetenceCard
                key={competence.id}
                competence={competence}
                onStart={handleStartCompetence}
                onContinue={handleContinueCompetence}
                sequentialNumber={index + 1}
                allCompetences={displayCompetences}
              />
            ))}
          </CompetencesGrid>
        </Content>
        <ModalLoader isLoading={loading} />
      </Container>
    </TestLayout>
  )
}

export default QuestionsTest360
